import { Collapse } from '@/components/Collapse/Collapse';
import { PromoCodeFooterLabel } from '@/components/PromoCodeFooterLabel/PromoCodeFooterLabel';
import { IconButton } from '@/components/IconButton';
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ClockIcon,
  ElevationIcon,
  LiftIcon,
  LocationIcon,
  MinusIcon,
  MountBlackIcon,
  MountBlueIcon,
  MountGreenIcon,
  MountRedIcon,
  PeopleIcon,
  PisteLengthIcon,
  PlusIcon,
} from '@/components/icons';
import { useSupplementalOfferLinesAcrossDomains } from '@/hooks/useSupplementalOfferLinesAcrossDomains';
import { useTripSkierCountsBaselineOffers } from '@/hooks/useTripSkierCountsBaselineOffers';
import { hydratePassSelectionsFromOrder, useCheckoutHydration } from '@/hooks/useCheckoutHydration';
import { useCalculateOffers, type DomainOffersEntry } from '@/pages/useCalculateOffers';
import {
  centsToMoney,
  pushDataLayerOnce,
  type GtmEcommerceItem,
} from '@/services/gtm-ecommerce.service';
import type { Domain, OfferItem } from '@/services/inventory-api.service';
import {
  getCheckoutSession,
  ordersService,
  type CheckoutPassSelectionDto,
  type PassSelectionPayload,
} from '@/services/orders.service';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { totalPeople, peopleByAgeCategoryLabel } from '@/utils/booking';
import { classNames } from '@/utils/classNames';
import { formatDateOnlyForDisplay } from '@/utils/date-only';
import { formatRestrictionLabel } from '@/utils/domain-restrictions';
import type { DomainRestriction } from '@/utils/domain-restrictions';
import { mergeDomainOffersForDisplay } from '@/utils/merge-offer-display-items';
import { offerCategoryMatchesOverride } from '@/utils/offer-category-match';
import {
  calculatePercentageDiscountForItem,
  calculatePromoTotals,
  resolvePromoDomainCoverage,
} from '@/utils/promo-pricing';
import { isStaticPromoInactiveReason } from '@/utils/promo-inactive-reason';
import { sanitizeSkierFormsForStepTwoToThree } from '@/utils/sanitize-skier-forms-step-transition';
import { getPassQuantity, getPassQuantityKey } from '@/utils/pass-quantity-key';
import { buildSkicardAddonQueuesByDomainAge, takeSkicardAddonPrice } from '@/utils/skicard-pricing';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import styles from './SkiPassOffersPage.module.css';

type SelectedPassLine = {
  domainId: number;
  ageCategory: string;
  domainTitle: string;
  passTitle: string;
  passShortTitle: string;
  quantity: number;
  total: number;
  promoDiscount: number;
  currency: string;
};

type SelectedPassUnit = {
  domainId: number;
  ageCategory: string;
  domainTitle: string;
  passTitle: string;
  passShortTitle: string;
  unitPrice: number;
  currency: string;
};

function getOfferCustomerType(item: OfferItem): string {
  return typeof item.customer_type === 'string' ? item.customer_type.trim() : '';
}

function getOfferTitle(item: OfferItem): string {
  if (typeof item.title !== 'string') {
    return '';
  }
  if (item.title.endsWith(' SKIPASS')) {
    const daysMatch = item.title.match(/^(\d+)-DAYS SKIPASS$/i);
    if (daysMatch) {
      return `${daysMatch[1]}-day pass`;
    }
  }
  return item.title;
}

function getOfferShortTitle(item: OfferItem): string {
  if (typeof item.title !== 'string') {
    return '';
  }
  if (item.title.endsWith(' SKIPASS')) {
    const daysMatch = item.title.match(/^(\d+)-DAYS SKIPASS$/i);
    if (daysMatch && daysMatch[1]) {
      const daysCount = parseInt(daysMatch[1], 10);
      return daysCount === 1 ? '1 day' : `${daysCount} days`;
    }
  }
  return item.title;
}

function isPassOfferItem(item: OfferItem): boolean {
  return item.type === 'pass' && getOfferCustomerType(item).length > 0;
}

function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatTripDate(isoDate: string): string {
  return formatDateOnlyForDisplay(isoDate, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatElevation(m: number | undefined): string {
  if (m == null) return '–';
  return Math.round(m).toLocaleString();
}

function formatSlopeDistance(lengthInKm: number | undefined): string {
  if (lengthInKm == null) return '–';
  return String(Math.round(lengthInKm));
}

function passDescription(item: OfferItem, domainTitle: string): string {
  const title = getOfferTitle(item).toLowerCase();
  if (title.includes('flexible')) {
    return `Allows ski days within a flexible window`;
  }
  if (title.includes('afternoon')) {
    return `Works from 11:30 AM`;
  }
  return `Full access to ${domainTitle} during your selected dates`;
}

function cloneAddonQueues(source: Map<string, number[]>): Map<string, number[]> {
  const cloned = new Map<string, number[]>();
  for (const [key, values] of source.entries()) {
    cloned.set(key, values.slice());
  }
  return cloned;
}

function formatEuroAmount(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function buildDynamicPromoMinAmountReason(
  minOrderAmount: number | null | undefined,
  currentSubtotalInCents: number
): string | undefined {
  if (minOrderAmount == null || !Number.isFinite(minOrderAmount) || minOrderAmount <= 0) {
    return undefined;
  }

  const minOrderAmountInCents = Math.round(minOrderAmount * 100);
  const shortfallInCents = minOrderAmountInCents - currentSubtotalInCents;
  if (shortfallInCents <= 0) {
    return undefined;
  }

  const priceDiff = Math.ceil(shortfallInCents / 100);
  return `Works from €${formatEuroAmount(minOrderAmount)}. Add €${String(priceDiff)} more.`;
}

function buildDynamicDomainInvalidReason(restrictedDomainNames: string[] | undefined): string {
  if (!restrictedDomainNames?.length) {
    return 'Not valid for selected ski passes.';
  }
  return `Valid for ${restrictedDomainNames.join(', ')} only.`;
}

function formatPromoTypeLabel(
  type: 'fixed' | 'percentage' | undefined,
  value: number | undefined
): string {
  if (type === 'percentage' && typeof value === 'number') {
    return `(-${String(value)}%)`;
  }
  if (type === 'fixed' && typeof value === 'number') {
    return `(-€${String(value)})`;
  }
  return '';
}

function resolveDisplayCode(
  promoCode?: string,
  pendingCode?: string,
  infoCode?: string
): string | undefined {
  const candidates = [promoCode, pendingCode, infoCode];
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function debugPassSyncLog(event: string, payload: Record<string, unknown>): void {
  if (typeof globalThis === 'undefined') return;
  const debugEnabled = Boolean((globalThis as { __B2C_DEBUG_PASS_SYNC?: boolean }).__B2C_DEBUG_PASS_SYNC);
  if (!debugEnabled) return;
  console.info(`[pass-sync][offers] ${event}`, payload);
}

type PassCardProps = {
  item: OfferItem;
  domainId: number;
  domainTitle: string;
  itemIndex: number;
  quantity: number;
  displayUnitPrice: number;
  currency: string;
  restrictions: Domain['restrictions'];
  onQuantityChange: (domainId: number, itemIndex: number, qty: number, item: OfferItem) => void;
};

function PassCard({
  item,
  domainId,
  domainTitle,
  itemIndex,
  quantity,
  displayUnitPrice,
  currency,
  restrictions,
  onQuantityChange,
}: PassCardProps) {
  const pricePerPerson = displayUnitPrice;
  const description = passDescription(item, domainTitle);
  const isSelected = quantity > 0;

  const matchedRestriction = restrictions?.find(
    (r) =>
      (r.type === 'age' || r.type === 'date') &&
      r.name.trim().toLowerCase() === getOfferCustomerType(item).toLowerCase()
  );
  const ageRestrictionLabel = matchedRestriction
    ? formatRestrictionLabel(matchedRestriction)
    : null;

  const handleCardClick = () => {
    if (!isSelected) onQuantityChange(domainId, itemIndex, 1, item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isSelected && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onQuantityChange(domainId, itemIndex, 1, item);
    }
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const customerType = getOfferCustomerType(item);

  return (
    <div
      className={classNames(
        styles.passCard,
        isSelected ? styles.passCardSelected : styles.passCardSelectable
      )}
      onClick={handleCardClick}
      role={!isSelected ? 'button' : undefined}
      tabIndex={!isSelected ? 0 : undefined}
      onKeyDown={!isSelected ? handleKeyDown : undefined}
    >
      <div className={styles.passCardBody}>
        <span className={styles.passCardDomain}>{domainTitle}</span>
        <div className={styles.passCardMeta}>
          <h4 className={styles.passCardType}>{customerType} {getOfferTitle(item)}</h4>
          <span className={styles.passCardDescription}>{description}</span>
        </div>
        <div className={styles.passCardPriceRow}>
          <div className={styles.passCardPriceRowLeft}>
            <span className={styles.passCardPrice}>{formatPrice(pricePerPerson, currency)}</span>
            <span className={styles.passCardPerPerson}>per {customerType.toLowerCase()}{ageRestrictionLabel && (
              <span className={styles.passCardAgeRestriction}> ({ageRestrictionLabel})</span>
            )}</span>
          </div>
          <div className={styles.passCardQtyWrapper} onClick={stopPropagation} role="presentation">
            <IconButton
              variant="subtle"
              icon={<MinusIcon />}
              aria-label={`Decrease ${item.customer_type} quantity`}
              onClick={() => onQuantityChange(domainId, itemIndex, Math.max(0, quantity - 1), item)}
              disabled={quantity <= 0}
            />
            <span
              className={classNames(
                styles.passCardQtyCount,
                isSelected && styles.passCardQtyCountActive
              )}
            >
              {quantity}
            </span>
            <IconButton
              variant="subtle"
              icon={<PlusIcon />}
              aria-label={`Increase ${item.customer_type} quantity`}
              onClick={() => onQuantityChange(domainId, itemIndex, quantity + 1, item)}
            />
          </div>
        </div>
        </div>
    </div>
  );
}

type AreaSectionProps = {
  domain: Domain;
  offers: DomainOffersEntry['offers'];
  selectedQuantities: Record<string, number>;
  daysOfSkiing: number | null;
  onQuantityChange: (domainId: number, itemIndex: number, qty: number, item: OfferItem) => void;
};

function AreaSection({
  domain,
  offers,
  selectedQuantities,
  daysOfSkiing,
  onQuantityChange,
}: AreaSectionProps) {
  const passOptionsSectionRef = useRef<HTMLDivElement | null>(null);
  const expandScrollTimeoutRef = useRef<number | null>(null);
  const resorts = domain.resorts ?? [];
  const resortNames = resorts.map((r) => r.title).join(' • ');
  const low = domain.low;
  const high = domain.high;
  const allLength = domain.all_length;
  const liftAmount = domain.lift_amount;
  const greenAmt = domain.green_amount;
  const blueAmt = domain.blue_amount;
  const redAmt = domain.red_amount;
  const blackAmt = domain.black_amount;
  const hasGreen = greenAmt != null && greenAmt > 0;
  const hasBlue = blueAmt != null && blueAmt > 0;
  const hasRed = redAmt != null && redAmt > 0;
  const hasBlack = blackAmt != null && blackAmt > 0;

  const skicardAddonQueues = useMemo(
    () =>
      buildSkicardAddonQueuesByDomainAge([
        {
          domain,
          offers: {
            items: offers.items,
            total: offers.total,
            currency: offers.currency,
            people: offers.people,
          },
        },
      ]),
    [domain, offers]
  );

  const passDisplayPriceByIndex = useMemo(() => {
    const map = new Map<number, number>();
    for (let idx = 0; idx < offers.items.length; idx++) {
      const item = offers.items[idx];
      if (!item || !isPassOfferItem(item)) continue;
      const baseUnitPrice = item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price;
      const addon = takeSkicardAddonPrice(
        cloneAddonQueues(skicardAddonQueues),
        domain.pk,
        getOfferCustomerType(item)
      );
      map.set(idx, baseUnitPrice + addon);
    }
    return map;
  }, [offers.items, skicardAddonQueues, domain.pk]);

  const areaSelection = offers.items
    .map((item, originalIndex) => ({ item, originalIndex }))
    .filter(({ item }) => isPassOfferItem(item))
    .map(({ item, originalIndex }) => {
      const qty = getPassQuantity(selectedQuantities, domain.pk, item, originalIndex);
      return qty > 0 ? { item, qty } : null;
    })
    .filter((x): x is { item: OfferItem; qty: number } => x != null);

  const selectionTotal = useMemo(() => {
    const queues = cloneAddonQueues(skicardAddonQueues);
    return areaSelection.reduce((sum, { item, qty }) => {
      const baseUnitPrice = item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price;
      const ageCategory = getOfferCustomerType(item);
      let lineTotal = 0;
      for (let i = 0; i < qty; i++) {
        lineTotal += baseUnitPrice + takeSkicardAddonPrice(queues, domain.pk, ageCategory);
      }
      return sum + lineTotal;
    }, 0);
  }, [areaSelection, skicardAddonQueues, domain.pk]);

  const hasSelection = areaSelection.length > 0;

  const passItems = offers.items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item.type === 'pass');
  const minOfferPrice = passItems.reduce<number | null>((min, { item, idx }) => {
    const unitPrice =
      passDisplayPriceByIndex.get(idx) ??
      (item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price);
    if (min == null) return unitPrice;
    return unitPrice < min ? unitPrice : min;
  }, null);

  const badgePrice = hasSelection
    ? formatPrice(selectionTotal, offers.currency)
    : `from ${formatPrice(minOfferPrice ?? offers.total, offers.currency)}`;
  const badgePassDesc = areaSelection
    .map(({ item, qty }) => `${item.customer_type}×${String(qty)}`)
    .join(', ');

  const headerSummary = (
    <div className={styles.areaHeaderCard}>
      <div className={styles.areaHeaderLeft}>
        <div className={styles.areaHeaderTitleBlock}>
          <h3 className={styles.areaHeaderTitle}>{domain.title}</h3>
          {resortNames && <p className={styles.areaHeaderResorts}>{resortNames}</p>}
        </div>
        <div className={styles.areaHeaderStats}>
          <div className={styles.areaStatsRow}>
            {allLength != null && allLength > 0 && (
              <div className={styles.areaStat}>
                <PisteLengthIcon className={styles.areaStatIcon} aria-hidden />
                <span>~{formatSlopeDistance(allLength)} km</span>
              </div>
            )}
            {low != null && high != null && (
              <div className={styles.areaStat}>
                <ElevationIcon className={styles.areaStatIcon} aria-hidden />
                <span>
                  {formatElevation(low)} – {formatElevation(high)} m
                </span>
              </div>
            )}
            {liftAmount != null && liftAmount > 0 && (
              <div className={styles.areaStat}>
                <LiftIcon className={styles.areaStatIcon} aria-hidden />
                <span>{String(liftAmount)} lifts</span>
              </div>
            )}
          </div>
          {(hasGreen || hasBlue || hasRed || hasBlack) && (
            <div className={styles.areaSlopesRow}>
              {hasGreen && (
                <div className={styles.areaSlopeStat}>
                  <MountGreenIcon className={styles.areaSlopeIcon} aria-hidden />
                  <span>{String(greenAmt)}</span>
                </div>
              )}
              {hasBlue && (
                <div className={styles.areaSlopeStat}>
                  <MountBlueIcon className={styles.areaSlopeIcon} aria-hidden />
                  <span>{String(blueAmt)}</span>
                </div>
              )}
              {hasRed && (
                <div className={styles.areaSlopeStat}>
                  <MountRedIcon className={styles.areaSlopeIcon} aria-hidden />
                  <span>{String(redAmt)}</span>
                </div>
              )}
              {hasBlack && (
                <div className={styles.areaSlopeStat}>
                  <MountBlackIcon className={styles.areaSlopeIcon} aria-hidden />
                  <span>{String(blackAmt)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.areaHeaderPhotoWrap}>
        <div
          className={styles.areaHeaderPhotoInner}
          style={
            domain.image?.trim()
              ? {
                  backgroundImage: `url(${domain.image.trim()})`,
                }
              : undefined
          }
        />
        <div
          className={classNames(
            styles.areaHeaderPriceBadge,
            hasSelection && styles.areaHeaderPriceBadgeSelected
          )}
        >
          <div className={styles.areaHeaderPriceBadgeTop}>
            <span className={styles.areaHeaderPriceText}>{badgePrice}</span>
            <ChevronDownIcon
              className={classNames(
                styles.areaCollapseChevron,
                hasSelection && styles.areaCollapseChevronDark
              )}
              aria-hidden
            />
          </div>
          {hasSelection && (
            <div className={styles.areaHeaderPriceBadgeDesc}>
              <span>{badgePassDesc}</span>
              {daysOfSkiing != null && daysOfSkiing > 0 && <span>{String(daysOfSkiing)} days</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const scrollToPassOptionsSection = useCallback(() => {
    if (typeof window === 'undefined') return;
    const passOptionsSection = passOptionsSectionRef.current;
    if (!passOptionsSection) return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const offset = isMobile ? 50 : 100;
    const top = passOptionsSection.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) return;
      if (expandScrollTimeoutRef.current != null) {
        window.clearTimeout(expandScrollTimeoutRef.current);
        expandScrollTimeoutRef.current = null;
      }

      requestAnimationFrame(() => {
        scrollToPassOptionsSection();
      });

      // Collapse content animates in; scroll again after animation so final position is exact.
      expandScrollTimeoutRef.current = window.setTimeout(() => {
        scrollToPassOptionsSection();
        expandScrollTimeoutRef.current = null;
      }, 180);
    },
    [scrollToPassOptionsSection]
  );

  useEffect(() => {
    return () => {
      if (expandScrollTimeoutRef.current != null) {
        window.clearTimeout(expandScrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className={styles.areaSection}>
      <Collapse summary={headerSummary} defaultOpen={false} onOpenChange={handleOpenChange}>
        <div ref={passOptionsSectionRef} className={styles.passOptionsSection}>
          {offers.items.length > 0 && (
            <div className={styles.passOptionsBlock}>
              <div className={styles.passCardScrollWrapper}>
                <div className={styles.passCardList}>
                  {offers.items
                    .map((item, originalIndex) => ({ item, originalIndex }))
                    .filter(({ item }) => isPassOfferItem(item))
                    .sort((a, b) => {
                      const aIsAdult = getOfferCustomerType(a.item).toLowerCase() === 'adult' ? 0 : 1;
                      const bIsAdult = getOfferCustomerType(b.item).toLowerCase() === 'adult' ? 0 : 1;
                      return aIsAdult - bIsAdult;
                    })
                    .map(({ item, originalIndex }) => (
                      <PassCard
                        key={`${String(domain.pk)}-${String(originalIndex)}-${getOfferCustomerType(item).toLowerCase()}`}
                        item={item}
                        domainId={domain.pk}
                        domainTitle={domain.title}
                        itemIndex={originalIndex}
                        quantity={getPassQuantity(selectedQuantities, domain.pk, item, originalIndex)}
                        displayUnitPrice={
                          passDisplayPriceByIndex.get(originalIndex) ??
                          (item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price)
                        }
                        currency={offers.currency}
                        restrictions={domain.restrictions}
                        onQuantityChange={onQuantityChange}
                      />
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </section>
  );
}

function PassProgressRing({ current, total }: { current: number; total: number }) {
  const r = 15.9;
  const circ = 2 * Math.PI * r;
  const ratio = total > 0 ? Math.min(current / total, 1) : 0;
  const offset = circ * (1 - ratio);

  return (
    <div className={styles.progressRing} aria-label={`${current} of ${total} passes selected`}>
      <svg viewBox="0 0 36 36" className={styles.progressRingSvg} aria-hidden>
        <circle
          cx="18"
          cy="18"
          r={r}
          className={styles.progressTrack}
          strokeWidth="3.2"
          fill="none"
        />
        {total > 0 && (
          <circle
            cx="18"
            cy="18"
            r={r}
            className={styles.progressFill}
            strokeWidth="3.2"
            fill="none"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 18 18)"
          />
        )}
      </svg>
      <div className={styles.progressRingLabel} aria-hidden>
        <span className={styles.progressCurrent}>{current}</span>
        <span className={styles.progressTotal}>/{total}</span>
      </div>
    </div>
  );
}

export const SkiPassOffersPage = () => {
  const navigate = useNavigate();
  const stepTwo = useBookSkiPassStore((s) => s.stepTwoFormFieldsValues);
  const referenceResponseData = useBookSkiPassStore((s) => s.referenceResponseData);
  const resorts = useBookSkiPassStore((s) => s.resorts);
  const selectedPassQuantities = useBookSkiPassStore((s) => s.selectedPassQuantities);
  const setPassQuantity = useBookSkiPassStore((s) => s.setPassQuantity);
  const updateStepThreeFormFields = useBookSkiPassStore((s) => s.updateStepThreeFormFields);
  const passSelectionOverrides = useBookSkiPassStore(
    (s) => s.stepThreeFormFieldsValues.passSelectionOverrides
  );
  const skierForms = useBookSkiPassStore((s) => s.stepThreeFormFieldsValues.skierForms);
  const selectedDomain = useBookSkiPassStore((s) => s.selectedDomain);
  const stepOnePromoCode = useBookSkiPassStore((s) => s.stepOneFormFieldsValues.promoCode);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hydrationError, setHydrationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  const detailsPopoverRef = useRef<HTMLDivElement | null>(null);

  const { order, loading: checkoutHydrationLoading } = useCheckoutHydration({
    hydrateTripDetails: true,
    onError: setHydrationError,
  });

  const effectivePassSelections = useMemo(() => {
    if (passSelectionOverrides != null && passSelectionOverrides.length > 0) {
      return passSelectionOverrides;
    }
    return order?.passSelections ?? [];
  }, [passSelectionOverrides, order?.passSelections]);

  const {
    dataByDomain: primaryOffersByDomain,
    loading,
    error: errorMessage,
  } = useCalculateOffers({
    passSelections: effectivePassSelections.length > 0 ? effectivePassSelections : undefined,
    skipPersonBirthDates: false,
  });

  const hasAnySelectedPassQuantities = useMemo(
    () => Object.values(selectedPassQuantities).some((qty) => qty > 0),
    [selectedPassQuantities]
  );

  const baselineOffersByDomain = useTripSkierCountsBaselineOffers(
    effectivePassSelections.length > 0 || hasAnySelectedPassQuantities
  );

  const mergedPrimaryAndBaseline = useMemo(
    () => mergeDomainOffersForDisplay(primaryOffersByDomain, baselineOffersByDomain),
    [primaryOffersByDomain, baselineOffersByDomain]
  );

  const dataByDomain = useSupplementalOfferLinesAcrossDomains({
    mergedByDomain: mergedPrimaryAndBaseline,
    passSelections: effectivePassSelections,
    skierForms,
    stepTwoStartingDate: stepTwo.startingDate,
    stepTwoDaysOfSkiing: stepTwo.daysOfSkiing,
    stepTwoSkierCounts: stepTwo.skierCounts,
    selectedDomain,
  });

  const lastPassOverrideSyncKeyRef = useRef<string | null>(null);
  const lastOrderHydrationKeyRef = useRef<string | null>(null);
  const hasManualQuantityEditsRef = useRef(false);

  useEffect(() => {
    if (hasManualQuantityEditsRef.current) {
      debugPassSyncLog('overrides-sync.skip.manual-edits', {});
      return;
    }
    if (passSelectionOverrides == null) {
      lastPassOverrideSyncKeyRef.current = null;
      return;
    }
    if (dataByDomain.length === 0) return;

    const overridesKey = JSON.stringify(
      passSelectionOverrides.map((o) => ({
        d: o.domain.id,
        p: o.passOfferId,
        c: o.ageCategory,
        q: o.quantity,
      }))
    );

    const offerLinesKey = dataByDomain
      .flatMap(({ domain, offers }) =>
        offers.items
          .filter((item) => isPassOfferItem(item))
          .map(
          (item) =>
            `${String(domain.pk)}:${String(item.pass_pk ?? '')}:${getOfferCustomerType(item).toLowerCase()}`
          )
      )
      .join('|');

    const syncKey = `${overridesKey}|${offerLinesKey}`;
    if (syncKey === lastPassOverrideSyncKeyRef.current) return;
    lastPassOverrideSyncKeyRef.current = syncKey;
    debugPassSyncLog('overrides-sync.start', {
      domains: dataByDomain.length,
      overridesCount: passSelectionOverrides.length,
      syncKey,
    });

    for (const { domain, offers } of dataByDomain) {
      const domainPassItems = offers.items
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => isPassOfferItem(item));

      const overridesForDomain = passSelectionOverrides.filter((o) => o.domain.id === domain.pk);

      const assignedByItemIndex = new Map<number, number>();
      let matchedOverridesCount = 0;

      const addAssignedQty = (itemIndex: number, qty: number) => {
        if (qty <= 0) return;
        assignedByItemIndex.set(itemIndex, (assignedByItemIndex.get(itemIndex) ?? 0) + qty);
      };

      for (const override of overridesForDomain) {
        const qty = Math.max(0, override.quantity);
        if (qty <= 0) continue;

        const matchedByPassAndCategory =
          override.passOfferId != null
            ? domainPassItems.find(
                ({ item }) =>
                  item.pass_pk === override.passOfferId &&
                  offerCategoryMatchesOverride(getOfferCustomerType(item), override.ageCategory)
              )
            : undefined;

        if (matchedByPassAndCategory) {
          matchedOverridesCount += 1;
          addAssignedQty(matchedByPassAndCategory.idx, qty);
          continue;
        }

        const matchedByPassPk =
          override.passOfferId != null
            ? (() => {
                const passPkMatches = domainPassItems.filter(
                  ({ item }) => item.pass_pk === override.passOfferId
                );
                return passPkMatches.length === 1 ? passPkMatches[0] : undefined;
              })()
            : undefined;

        if (matchedByPassPk) {
          matchedOverridesCount += 1;
          addAssignedQty(matchedByPassPk.idx, qty);
          continue;
        }

        const matchedByCategory = domainPassItems.find(({ item }) =>
          offerCategoryMatchesOverride(getOfferCustomerType(item), override.ageCategory)
        );

        if (matchedByCategory) {
          matchedOverridesCount += 1;
          addAssignedQty(matchedByCategory.idx, qty);
        }
      }

      if (overridesForDomain.length > 0 && matchedOverridesCount === 0) {
        continue;
      }

      for (let idx = 0; idx < offers.items.length; idx++) {
        const item = offers.items[idx];
        if (!item) continue;
        if (!isPassOfferItem(item)) continue;
        const qty = assignedByItemIndex.get(idx) ?? 0;

        setPassQuantity(domain.pk, qty, {
          passPk: item.pass_pk,
          customerType: getOfferCustomerType(item),
          quantityKey: getPassQuantityKey(domain.pk, item, idx),
          itemIndex: idx,
          source: 'offers:overrides-sync',
        });
      }
    }
  }, [dataByDomain, passSelectionOverrides, setPassQuantity]);

  const domainsWithOffers = useMemo(
    () => dataByDomain.filter(({ offers }) => offers.items.some((item) => isPassOfferItem(item))),
    [dataByDomain]
  );

  const gtmViewItems: GtmEcommerceItem[] = useMemo(
    () =>
      domainsWithOffers.flatMap(({ domain, offers }) =>
        offers.items.filter((item) => isPassOfferItem(item)).map((item) => {
          const unitPriceCents =
            item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price;
          const customerType = getOfferCustomerType(item);
          const title = getOfferTitle(item);
          return {
            item_id:
              item.pass_pk != null
                ? String(item.pass_pk)
                : `${String(domain.pk)}:${customerType}:${title}`,
            item_name: `${domain.title} ${title}`.trim(),
            price: centsToMoney(unitPriceCents),
            quantity: item.quantity > 0 ? item.quantity : 1,
            currency: offers.currency,
          };
        })
      ),
    [domainsWithOffers]
  );

  useEffect(() => {
    if (checkoutHydrationLoading || gtmViewItems.length === 0) return;
    const firstItem = gtmViewItems[0];
    if (!firstItem) return;
    const key = `view_item:${gtmViewItems.map((item) => item.item_id).join('|')}`;
    pushDataLayerOnce(
      key,
      {
        event: 'view_item',
        ecommerce: {
          currency: firstItem.currency,
          items: gtmViewItems,
        },
      },
      {
        booking_ref: order?.reference,
        promocode: order?.promoCode,
      }
    );
  }, [checkoutHydrationLoading, gtmViewItems, order?.reference, order?.promoCode]);

  useEffect(() => {
    if (!order || dataByDomain.length === 0) return;
    if (hasManualQuantityEditsRef.current) {
      debugPassSyncLog('hydrate.skip.manual-edits', { orderId: order.orderId });
      return;
    }
    if (
      passSelectionOverrides == null &&
      hasAnySelectedPassQuantities &&
      lastOrderHydrationKeyRef.current != null
    ) {
      debugPassSyncLog('hydrate.skip.local-quantities', {
        orderId: order.orderId,
        hasAnySelectedPassQuantities,
      });
      return;
    }

    const passSelectionsKey = JSON.stringify(
      (order.passSelections ?? []).map((selection) => ({
        domainId: selection.domain.id,
        passOfferId: selection.passOfferId ?? null,
        ageCategory: selection.ageCategory,
        quantity: selection.quantity,
      }))
    );

    const offerLinesKey = dataByDomain
      .flatMap(({ domain, offers }) =>
        offers.items
          .filter((item) => isPassOfferItem(item))
          .map((item) => `${String(domain.pk)}:${String(item.pass_pk ?? '')}`)
      )
      .join('|');

    const hydrationKey = `${String(order.orderId)}:${passSelectionsKey}:${offerLinesKey}`;
    if (lastOrderHydrationKeyRef.current === hydrationKey) return;
    lastOrderHydrationKeyRef.current = hydrationKey;

    debugPassSyncLog('hydrate.run', {
      orderId: order.orderId,
      hydrationKey,
      passSelectionsCount: (order.passSelections ?? []).length,
    });

    hydratePassSelectionsFromOrder(order, dataByDomain);
  }, [order, dataByDomain, hasAnySelectedPassQuantities, passSelectionOverrides]);

  const startLabel = stepTwo.startingDate ? formatTripDate(stepTwo.startingDate) : '–';
  const durationLabel = !stepTwo.daysOfSkiing ? '–' : Number(stepTwo.daysOfSkiing) > 1 ? `${stepTwo.daysOfSkiing} days` : '1 day';
  const peopleCount = totalPeople(stepTwo.skierCounts);
  const groupLabel = peopleCount === 1 ? '1 person' : `${String(peopleCount)} people`;
  const groupLabel2 = peopleByAgeCategoryLabel(stepTwo.skierCounts);
  const resortName = referenceResponseData?.resort ?? '';

  const connectedDomainIds = useMemo(() => {
    let originalDomains = referenceResponseData?.domains;
    if (!originalDomains?.length) {
      const resortWithDomain = resorts.find((r) => r.priority_domain);
      originalDomains = resortWithDomain?.domains;
    }
    return new Set((originalDomains ?? []).map((d) => d.id));
  }, [referenceResponseData?.domains, resorts]);

  const handleQuantityChange = useCallback(
    (domainId: number, itemIndex: number, qty: number, item: OfferItem) => {
      hasManualQuantityEditsRef.current = true;
      updateStepThreeFormFields({ passSelectionOverrides: null });
      debugPassSyncLog('user-edit', {
        domainId,
        itemIndex,
        category: getOfferCustomerType(item),
        passPk: item.pass_pk ?? null,
        qty,
      });
      setPassQuantity(domainId, qty, {
        passPk: item.pass_pk,
        customerType: getOfferCustomerType(item),
        quantityKey: getPassQuantityKey(domainId, item, itemIndex),
        itemIndex,
        source: 'offers:user-edit',
      });
    },
    [setPassQuantity, updateStepThreeFormFields]
  );

  const selectedPassUnits = useMemo((): SelectedPassUnit[] => {
    const skicardAddonQueues = buildSkicardAddonQueuesByDomainAge(dataByDomain);
    const units: SelectedPassUnit[] = [];

    for (const { domain, offers } of domainsWithOffers) {
      for (let idx = 0; idx < offers.items.length; idx++) {
        const item = offers.items[idx];
        if (!item || !isPassOfferItem(item)) continue;

        const qty = getPassQuantity(selectedPassQuantities, domain.pk, item, idx);
        if (qty <= 0) continue;

        const baseUnitPrice = item.quantity > 0 ? Math.round(item.price / item.quantity) : item.price;
        const ageCategory = getOfferCustomerType(item);

        for (let i = 0; i < qty; i++) {
          units.push({
            domainId: domain.pk,
            ageCategory,
            domainTitle: domain.title,
            passTitle: getOfferTitle(item),
            passShortTitle: getOfferShortTitle(item),
            unitPrice:
              baseUnitPrice +
              takeSkicardAddonPrice(skicardAddonQueues, domain.pk, ageCategory),
            currency: offers.currency,
          });
        }
      }
    }

    return units;
  }, [domainsWithOffers, selectedPassQuantities, dataByDomain]);

  const promoTotals = useMemo(() => {
    const promoItems = selectedPassUnits.map((line) => ({
      unitPrice: line.unitPrice,
      domainId: line.domainId,
    }));
    return calculatePromoTotals(promoItems, order?.promoCodeInfo);
  }, [selectedPassUnits, order?.promoCodeInfo]);

  const originalTotal = promoTotals.subtotal;
  const grandTotal = promoTotals.grandTotal;
  const promoDiscountAmount = promoTotals.promoDiscountAmount;

  const effectivePromoCodeInfo = useMemo(() => {
    const promoInfo = order?.promoCodeInfo;
    if (!promoInfo) return promoInfo;

    const hasLiveDiscount = grandTotal !== originalTotal;
    const domainCoverage = resolvePromoDomainCoverage(selectedPassUnits, promoInfo);
    const dynamicMinAmountReason = buildDynamicPromoMinAmountReason(
      promoInfo.minOrderAmount,
      originalTotal
    );

    const reasons:string[] = [];
    let inactiveReason: string | undefined;
    if (!hasLiveDiscount) {
      if (isStaticPromoInactiveReason(promoInfo.inactiveReason)) {
        inactiveReason = promoInfo.inactiveReason;
      } else {
        if (domainCoverage.domainRestrictionInvalid) {
          reasons.push(buildDynamicDomainInvalidReason(promoInfo.restrictedDomainNames));
        }
        if (dynamicMinAmountReason) {
          reasons.push(dynamicMinAmountReason);
        }
        inactiveReason = reasons.join(' ');
      }
    }

    return {
      ...promoInfo,
      status: hasLiveDiscount ? ('active' as const) : ('inactive' as const),
      inactiveReason,
      domainRestrictionPartial: domainCoverage.domainRestrictionPartial,
      domainRestrictionInvalid: domainCoverage.domainRestrictionInvalid,
    };
  }, [order?.promoCodeInfo, grandTotal, originalTotal, selectedPassUnits]);

  const isPercentagePromoApplied =
    effectivePromoCodeInfo?.status === 'active' &&
    effectivePromoCodeInfo?.type === 'percentage' &&
    promoDiscountAmount > 0;

  const promoDiscountByLineKey = useMemo(() => {
    const map = new Map<string, number>();
    if (!isPercentagePromoApplied || !effectivePromoCodeInfo) return map;

    for (const unit of selectedPassUnits) {
      const key = `${String(unit.domainId)}|${unit.ageCategory}|${unit.passTitle}|${unit.currency}`;
      const discount = calculatePercentageDiscountForItem(
        effectivePromoCodeInfo,
        unit.unitPrice,
        unit.domainId,
        originalTotal
      );
      if (discount <= 0) continue;
      map.set(key, (map.get(key) ?? 0) + discount);
    }

    return map;
  }, [isPercentagePromoApplied, effectivePromoCodeInfo, selectedPassUnits, originalTotal]);

  const selectedPassesList: SelectedPassLine[] = useMemo(() => {
    const grouped = new Map<string, SelectedPassLine>();

    for (const unit of selectedPassUnits) {
      const key = `${String(unit.domainId)}|${unit.ageCategory}|${unit.passTitle}|${unit.currency}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += 1;
        existing.total += unit.unitPrice;
        existing.promoDiscount = promoDiscountByLineKey.get(key) ?? existing.promoDiscount;
      } else {
        grouped.set(key, {
          domainId: unit.domainId,
          ageCategory: unit.ageCategory,
          domainTitle: unit.domainTitle,
          passTitle: unit.passTitle,
          passShortTitle: unit.passShortTitle,
          quantity: 1,
          total: unit.unitPrice,
          promoDiscount: promoDiscountByLineKey.get(key) ?? 0,
          currency: unit.currency,
        });
      }
    }

    return [...grouped.values()];
  }, [selectedPassUnits, promoDiscountByLineKey]);

  const currency = selectedPassesList[0]?.currency ?? 'EUR';
  const hasSelection = selectedPassesList.length > 0;
  const selectedPassesTotalCount = selectedPassesList.reduce((sum, p) => sum + p.quantity, 0);
  const showDetailsMinItems = isMobileViewport ? 1 : 3;
  const canShowDetailsToggle = selectedPassesList.length >= showDetailsMinItems;
  const bubblePromoCode = resolveDisplayCode(
    order?.promoCode,
    stepOnePromoCode || undefined,
    effectivePromoCodeInfo?.code
  );
  const bubblePromoTypeLabel = formatPromoTypeLabel(
    effectivePromoCodeInfo?.type,
    effectivePromoCodeInfo?.value
  );
  const bubblePromoInfoText = effectivePromoCodeInfo?.domainRestrictionPartial
    ? 'Applied only to eligible ski passes.'
    : effectivePromoCodeInfo?.inactiveReason;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!showDetails) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode) return;
      if (detailsPopoverRef.current?.contains(targetNode)) return;
      setShowDetails(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDetails(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showDetails]);

  useEffect(() => {
    if (showDetails && (!hasSelection || !canShowDetailsToggle)) {
      setShowDetails(false);
    }
  }, [showDetails, hasSelection, canShowDetailsToggle]);

  const passSelections = useMemo((): PassSelectionPayload[] => {
    return domainsWithOffers.flatMap(({ domain, offers }) => {
      if (!domain.title) return [];
      return offers.items.reduce<PassSelectionPayload[]>((acc, item, idx) => {
        if (!isPassOfferItem(item)) return acc;
        const qty = getPassQuantity(selectedPassQuantities, domain.pk, item, idx);
        if (qty <= 0) return acc;
        acc.push({
          domain: { id: domain.pk, name: domain.title },
          ageCategory: getOfferCustomerType(item),
          passOfferId: item.pass_pk,
          passTitle: getOfferTitle(item),
          quantity: qty,
        });
        return acc;
      }, []);
    });
  }, [domainsWithOffers, selectedPassQuantities]);

  const handleContinue = useCallback(() => {
    const submit = async () => {
      if (!hasSelection) return;
      setSubmitError(null);
      const session = getCheckoutSession();
      if (!session) {
        void navigate('/trip-details', { replace: true });
        return;
      }
      if (passSelections.length === 0) {
        setSubmitError('Please select at least one pass.');
        return;
      }

      setIsSubmitting(true);
      try {
        const previousOrderPassSelections = order?.passSelections ?? [];
        const nextPassSelections: CheckoutPassSelectionDto[] = passSelections.map((p) => ({
          domain: p.domain,
          ageCategory: p.ageCategory,
          passOfferId: p.passOfferId,
          passTitle: p.passTitle,
          quantity: p.quantity,
        }));

        const domainRestrictionsMap = new Map<number, DomainRestriction[]>();
        for (const { domain } of dataByDomain) {
          if (domain.restrictions?.length) {
            domainRestrictionsMap.set(domain.pk, domain.restrictions as DomainRestriction[]);
          }
        }

        const startDate = order?.tripDetails?.startDate ?? stepTwo.startingDate ?? '';

        await ordersService.updatePasses(session.orderId, session.checkoutToken, {
          selections: passSelections,
        });

        const currentForms = useBookSkiPassStore.getState().stepThreeFormFieldsValues.skierForms;
        const skierForms = sanitizeSkierFormsForStepTwoToThree(
          currentForms,
          previousOrderPassSelections,
          nextPassSelections,
          domainRestrictionsMap,
          startDate
        );

        updateStepThreeFormFields({ passSelectionOverrides: null, skierForms });
        void navigate('/booking-details');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save pass selections';
        setSubmitError(message);
      } finally {
        setIsSubmitting(false);
      }
    };

    void submit();
  }, [
    hasSelection,
    navigate,
    passSelections,
    updateStepThreeFormFields,
    order?.passSelections,
    order?.tripDetails?.startDate,
    dataByDomain,
    stepTwo.startingDate,
  ]);

  if (!checkoutHydrationLoading && !getCheckoutSession()) {
    return <Navigate to="/trip-details" replace />;
  }

  if (loading && domainsWithOffers.length === 0) {
    return (
      <div className={styles.content}>
        <p className={styles.message}>Calculating offers…</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={styles.content}>
        <p className={styles.error}>{errorMessage}</p>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.content}>
        {hydrationError && <p className={styles.error}>{hydrationError}</p>}
        <div className={styles.sectionBlock}>
          <div className={styles.sectionHeader}>
            <div className={styles.tripStrip}>
              <div className={styles.tripStripMobileTop}>
                <span className={styles.tripStripArea}>{resortName || '–'}</span>
                <Link to="/trip-details" className={styles.editTripLink}>
                  <ChevronLeftIcon className={styles.editTripChevron} aria-hidden />
                  Edit trip details
                </Link>
              </div>
              <div className={styles.tripStripMobileBottom}>
                <span className={styles.tripStripMobileMeta}>{startLabel}</span>
                <span className={styles.tripStripMobileDivider} aria-hidden />
                <span className={styles.tripStripMobileMeta}>{durationLabel}</span>
                <span className={styles.tripStripMobileDivider} aria-hidden />
                <span className={styles.tripStripMobileMeta}>{groupLabel}</span>
              </div>
              <div className={styles.tripStripDesktop}>
                <div className={styles.tripStripLeft}>
                  {resortName && (
                    <span className={styles.tripStripItem}>
                      <LocationIcon className={styles.tripStripIcon} aria-hidden />
                      <span className={styles.tripStripLabel}>Going to: </span>
                      <span className={styles.tripStripValue}>{resortName}</span>
                    </span>
                  )}
                  <span className={styles.tripStripItem}>
                    <CalendarIcon className={styles.tripStripIcon} aria-hidden />
                    <span className={styles.tripStripLabel}>Start: </span>
                    <span className={styles.tripStripValue}>{startLabel}</span>
                  </span>
                  <span className={styles.tripStripItem}>
                    <ClockIcon className={styles.tripStripIcon} aria-hidden />
                    <span className={styles.tripStripLabel}>Duration: </span>
                    <span className={styles.tripStripValue}>{durationLabel}</span>
                  </span>
                  <span className={styles.tripStripItem}>
                    <PeopleIcon className={styles.tripStripIcon} aria-hidden />
                    <span className={styles.tripStripLabel}>Group: </span>
                    <span className={styles.tripStripValue}>{groupLabel}</span>
                  </span>
                </div>
                <Link to="/trip-details" className={styles.editTripLinkDesktop}>
                  <ChevronLeftIcon className={styles.editTripChevron} aria-hidden />
                  Edit trip details
                </Link>
              </div>
            </div>

            <h2 className={styles.sectionTitle}>Select ski pass</h2>
            <p className={styles.sectionSubtitle}>
              Explore and combine ski passes from different areas. Open any area to select passes –
              your choices will be added below and totalled automatically
            </p>
          </div>

          {domainsWithOffers.length > 0 ? (
            <div className={styles.areaList}>
              {domainsWithOffers.map(({ domain, offers }) => {
                const isConnected =
                  connectedDomainIds.size === 0 || connectedDomainIds.has(domain.pk);
                const showWarning = !isConnected && Boolean(resortName);
                return (
                  <div
                    key={String(domain.pk)}
                    className={showWarning ? styles.domainWarningWrapper : undefined}
                  >
                    {showWarning && (
                      <div className={styles.domainWarningText}>
                        Not directly connected to {resortName}. Transfer required
                      </div>
                    )}
                    <AreaSection
                      domain={domain}
                      offers={offers}
                      selectedQuantities={selectedPassQuantities}
                      daysOfSkiing={stepTwo.daysOfSkiing ? Number(stepTwo.daysOfSkiing) : null}
                      onQuantityChange={handleQuantityChange}
                    />
                  </div>
                );
              })}
            </div>
          ) : dataByDomain.length > 0 ? (
            <p className={styles.message}>
              No ski passes are available for your trip details in these ski areas. Try adjusting
              the number of days or other options in trip details.
            </p>
          ) : (
            <p className={styles.message}>Select a ski area in trip details first.</p>
          )}
        </div>
      </div>

      {domainsWithOffers.length > 0 && (
        <footer className={styles.stickyFooter}>
          <div className={styles.footerInner}>
            <div className={styles.footerProgress}>
              <PassProgressRing current={selectedPassesTotalCount} total={peopleCount} />
              <div className={styles.footerSummaryContent}>
                {!hasSelection ? (
                  <p className={styles.footerEmptyHint}>Select passes for {groupLabel2}</p>
                ) : (
                  <>
                    {!isMobileViewport && selectedPassesList.slice(0, 2).map((p, i) => (
                      <span key={i} className={styles.footerSummaryLine}>
                        <span>
                          {p.ageCategory} × {p.quantity}
                        </span>
                        <span className={styles.footerSummaryLineDash}>•</span>
                        <span className={styles.footerSummaryLineName}>
                          {p.domainTitle}
                        </span>
                        <span className={styles.footerSummaryLineDash}>•</span>
                        <span>
                          {p.passShortTitle}
                        </span>
                        <span className={styles.footerSummaryLineDash}>—</span>
                        <span className={styles.footerSummaryLinePriceGroup}>
                          {isPercentagePromoApplied && p.promoDiscount > 0 && (
                            <span className={styles.footerSummaryLinePriceOriginal}>
                              {formatPrice(p.total, p.currency)}
                            </span>
                          )}
                          <span className={styles.footerSummaryLinePrice}>
                            {formatPrice(
                              isPercentagePromoApplied && p.promoDiscount > 0
                                ? Math.max(0, p.total - p.promoDiscount)
                                : p.total,
                              p.currency
                            )}
                          </span>
                        </span>
                      </span>
                    ))}
                    {canShowDetailsToggle && (
                      <div ref={detailsPopoverRef} className={styles.footerDetailsTriggerWrap}>
                        <button
                          type="button"
                          className={styles.footerSeeDetails}
                          aria-expanded={showDetails}
                          aria-controls="footer-pass-details-bubble"
                          onClick={() => setShowDetails((v) => !v)}
                        >
                          See details ({selectedPassesTotalCount})
                          <ChevronDownIcon
                            className={classNames(
                              styles.footerSeeDetailsIcon,
                              showDetails && styles.footerSeeDetailsIconOpen
                            )}
                            aria-hidden
                          />
                        </button>
                        {showDetails && (
                          <div
                            id="footer-pass-details-bubble"
                            role="dialog"
                            aria-label="Selected passes details"
                            className={styles.footerDetailsBubble}
                          >
                            <div className={styles.footerDetailsBubbleContent}>
                              {selectedPassesList.map((p, i) => (
                                <div key={`detail-${String(i)}`} className={styles.footerDetailsRow}>
                                  <div className={styles.footerDetailsTextBlock}>
                                    <span className={styles.footerDetailsTitle}>
                                      {p.ageCategory} {p.quantity}
                                    </span>
                                    <span className={styles.footerDetailsSubtitle}>
                                      {p.domainTitle} • {p.passShortTitle}
                                    </span>
                                  </div>
                                  <div className={styles.footerDetailsPriceBlock}>
                                    <span className={styles.footerDetailsPrice}>
                                      {formatPrice(p.total, p.currency)}
                                    </span>
                                    {isPercentagePromoApplied && p.promoDiscount > 0 && (
                                      <span className={styles.footerDetailsDiscount}>
                                        -{formatPrice(p.promoDiscount, p.currency)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}

                              <div className={styles.footerDetailsDivider} />

                              <div className={styles.footerDetailsRow}>
                                <span className={styles.footerDetailsSubtotalLabel}>Subtotal</span>
                                <span className={styles.footerDetailsSubtotalValue}>
                                  {formatPrice(originalTotal, currency)}
                                </span>
                              </div>

                              {bubblePromoCode && (
                                <div className={styles.footerDetailsPromoBlock}>
                                  <div className={styles.footerDetailsRow}>
                                    <span className={styles.footerDetailsPromoLabel}>
                                      Promo code {bubblePromoCode}
                                      {bubblePromoTypeLabel ? ` ${bubblePromoTypeLabel}` : ''}.
                                    </span>
                                    <span className={styles.footerDetailsDiscount}>
                                      {promoDiscountAmount > 0
                                        ? `-${formatPrice(promoDiscountAmount, currency)}`
                                        : formatPrice(0, currency)}
                                    </span>
                                  </div>
                                  {bubblePromoInfoText && (
                                    <p className={styles.footerDetailsPromoHint}>{bubblePromoInfoText}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {submitError && <p className={styles.footerError}>{submitError}</p>}
              </div>
              <div className={styles.footerTotal}>
                {originalTotal !== grandTotal && (
                  <span className={styles.footerTotalOriginal}>
                    {formatPrice(originalTotal, currency)}
                  </span>
                )}
                <span className={styles.footerTotalAmount}>
                  {originalTotal > 0 ? formatPrice(grandTotal, currency) : ''}
                </span>
              </div>
            </div>

            <div className={styles.footerRight}>
              <PromoCodeFooterLabel
                promoCode={order?.promoCode}
                promoCodeInfo={effectivePromoCodeInfo}
                pendingCode={stepOnePromoCode || undefined}
              />
              <div className={styles.footerButtonBlock}>
                <button
                  type="button"
                  className={styles.continueButton}
                  disabled={!hasSelection || isSubmitting}
                  onClick={handleContinue}
                >
                  {isSubmitting ? 'Saving selections…' : 'Continue with selected passes'}
                </button>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
